import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GuildInviteResponse } from '@/lib/api';

interface UseGuildInvitesReturn {
  invites: GuildInviteResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  create: (options?: { maxUses?: number; expiresInHours?: number }) => Promise<GuildInviteResponse>;
  revoke: (inviteId: string) => Promise<void>;
}

export function useGuildInvites(guildId: string | undefined): UseGuildInvitesReturn {
  const [invites, setInvites] = useState<GuildInviteResponse[]>([]);
  const [loading, setLoading] = useState(!!guildId);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchInvites = useCallback(async () => {
    if (!guildId) return;
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.listGuildInvites(guildId);
      if (fetchIdRef.current !== id) return;
      setInvites(result.invites);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load invites');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [guildId]);

  const create = useCallback(
    async (options?: { maxUses?: number; expiresInHours?: number }) => {
      if (!guildId) throw new Error('No guild ID');
      const invite = await api.createGuildInvite(guildId, options);
      await fetchInvites();
      return invite;
    },
    [guildId, fetchInvites],
  );

  const revoke = useCallback(
    async (inviteId: string) => {
      if (!guildId) return;
      await api.revokeGuildInvite(guildId, inviteId);
      await fetchInvites();
    },
    [guildId, fetchInvites],
  );

  useEffect(() => {
    fetchInvites();
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchInvites]);

  return {
    invites,
    loading,
    error,
    refresh: fetchInvites,
    create,
    revoke,
  };
}
